import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { LiquidityProvider as LiquidityProviderApi, LiquidityProviderControllerService } from '@api/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { toOptionItemModel } from '@shared/components/multi-select-with-exclude/multi-select-with-exclude.utils';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { keyBy } from 'lodash';
import { map, pipe, switchMap } from 'rxjs';
export interface LiquidityProvider extends LiquidityProviderApi {
  id: number;
  order: number;
}
export const useLiquidityProviders = () =>
  signalStoreFeature(
    {
      props: {
        _notificationService: type<NotificationService>(),
      },
    },
    withState({
      liquidityProviders: new Array<LiquidityProvider>(),
      liquidityProvidersLoaded: false,
    }),
    withProps(() => ({
      _liquidityProviderService: inject(LiquidityProviderControllerService),
    })),
    withComputed(store => {
      const liquidityProvidersMap = computed(() => keyBy(store.liquidityProviders(), 'id'));

      const liquidityProvidersOptions = computed(() =>
        store.liquidityProviders().map(lp => toOptionItemModel(lp, 'id', 'name')),
      );

      const krakenLp = computed(() => store.liquidityProviders().find(lp => lp.name === 'Kraken'));

      const bybitLp = computed(() => store.liquidityProviders().find(lp => lp.name === 'Bybit'));

      return {
        liquidityProvidersMap,
        liquidityProvidersOptions,
        krakenLp,
        bybitLp,
      };
    }),
    withMethods(store => {
      const loadLiquidityProviders = rxMethod<void>(
        pipe(
          switchMap(() =>
            store._liquidityProviderService.getLiquidityProviders({}).pipe(
              map(liquidityProviders => liquidityProviders.map(lp => lp as LiquidityProvider)),
              tapResponse({
                next: (liquidityProviders: LiquidityProvider[]) =>
                  patchState(store, {
                    liquidityProviders,
                    liquidityProvidersLoaded: true,
                  }),
                error: (error: HttpErrorResponse) => {
                  store._notificationService.showErrorNotification('Failed to load liquidity providers', error);
                  patchState(store, { liquidityProvidersLoaded: true });
                },
              }),
            ),
          ),
        ),
      );
      return {
        loadLiquidityProviders,
      };
    }),
  );
